import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CaretDown, CaretUp, Warning, UploadSimple, Sparkle, X, Check } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const GrantTracker = () => {
  const [grants, setGrants] = useState([]);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [showScanModal, setShowScanModal] = useState(false);
  const [scanning, setScanning] = useState(false);
  const fileInputRef = useRef(null);
  
  const [scannedGrant, setScannedGrant] = useState({
    name: '',
    funder: '',
    amount: '',
    start_date: '',
    end_date: '',
    reporting_deadline: '',
    sector: ''
  });

  useEffect(() => {
    fetchGrants();
  }, []);

  const fetchGrants = async () => {
    try {
      const response = await axios.get(`${API}/tracker`);
      setGrants(response.data);
    } catch (error) {
      console.error('Failed to fetch grants:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchGrantDetails = async (grantId) => {
    try {
      const response = await axios.get(`${API}/tracker/${grantId}`);
      setSelectedGrant(response.data);
      setExpandedId(grantId);
    } catch (error) {
      console.error('Failed to fetch grant details:', error);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setScanning(true);
    setShowScanModal(true);

    try {
      // Simulate extraction
      const response = await axios.post(`${API}/extract/grant`, {
        text_content: `Grant Agreement for ${file.name}. Mocked data extraction.`
      });

      setScannedGrant({
        ...response.data,
        amount: response.data.amount.toString(),
        start_date: new Date(response.data.start_date || Date.now()).toISOString().split('T')[0],
        end_date: new Date(response.data.end_date || Date.now()).toISOString().split('T')[0],
        reporting_deadline: new Date(response.data.reporting_deadline || Date.now()).toISOString().split('T')[0],
      });
    } catch (error) {
      console.error('Scan failed:', error);
      alert('AI Scan failed. Please try again or enter manually.');
    } finally {
      setScanning(false);
    }
  };

  const handleSaveScannedGrant = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/tracker`, {
        ...scannedGrant,
        grant_name: scannedGrant.name,
        amount: parseFloat(scannedGrant.amount),
        budget: parseFloat(scannedGrant.amount),
        spent: 0,
        utilization: 0,
        status: 'active',
        documents: ['agreement.pdf'],
        alerts: []
      });
      setShowScanModal(false);
      fetchGrants();
    } catch (error) {
      console.error('Failed to save grant:', error);
      alert('Failed to save grant');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'bg-[#22c55e]/15 text-[#22c55e]';
      case 'completed': return 'bg-[#3b82f6]/15 text-[#3b82f6]';
      case 'at_risk': return 'bg-[#ef4444]/15 text-[#ef4444]';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  const getUtilizationColor = (utilization) => {
    if (utilization < 30) return 'text-[#ef4444]';
    if (utilization > 90) return 'text-[#f59e0b]';
    return 'text-[#22c55e]';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-full"><div className="text-muted-foreground">Loading grants...</div></div>;
  }

  return (
    <div className="h-full overflow-auto p-4 sm:p-6 space-y-6 bg-background text-foreground transition-colors duration-300">
      <div className="border-b border-border pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4">
        <div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight mb-2">Grant Tracker</h1>
          <p className="text-sm sm:text-base text-muted-foreground">Monitor active grants and track utilization</p>
        </div>
        <div className="flex gap-3 w-full sm:w-auto">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept="application/pdf,image/*"
          />
          <button
            onClick={() => fileInputRef.current.click()}
            className="flex-1 sm:flex-none px-4 py-2 bg-card border border-[#E04E39]/30 text-[#E04E39] rounded-md flex items-center justify-center gap-2 hover:bg-[#E04E39]/10 transition-colors font-medium text-sm"
          >
            <UploadSimple size={20} weight="bold" />
            AI Scan
          </button>
        </div>
      </div>

      <div className="bg-card border border-border rounded-md overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[800px]">
            <thead>
              <tr className="border-b border-border bg-muted/50">
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Grant Name</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Funder</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Amount</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Status</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Utilization</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Spent</th>
                <th className="px-6 py-4 text-left text-xs font-bold uppercase tracking-wider text-muted-foreground">Deadline</th>
                <th className="px-6 py-4 text-center text-xs font-bold uppercase tracking-wider text-muted-foreground">Details</th>
              </tr>
            </thead>
            <tbody>
              {grants.map((grant, idx) => (
                <div key={grant.id} className="contents">
                  <tr 
                    onClick={() => expandedId === grant.id ? setExpandedId(null) : fetchGrantDetails(grant.id)}
                    className={`border-b border-border hover:bg-muted/30 cursor-pointer transition-all ${idx % 2 === 0 ? 'bg-card' : 'bg-muted/10'}`}
                  >
                    <td className="px-6 py-4 text-sm font-medium">{grant.grant_name}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{grant.funder}</td>
                    <td className="px-6 py-4 text-sm font-medium">${grant.amount.toLocaleString()}</td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 text-xs font-medium rounded uppercase ${getStatusColor(grant.status)}`}>
                        {grant.status}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden min-w-[60px]">
                          <div 
                            className="bg-[#E04E39] h-full rounded-full"
                            style={{ width: `${Math.min(grant.utilization, 100)}%` }}
                          />
                        </div>
                        <span className={`text-sm font-bold ${getUtilizationColor(grant.utilization)}`}>
                          {grant.utilization.toFixed(1)}%
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">${grant.spent.toLocaleString()}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {new Date(grant.reporting_deadline).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          expandedId === grant.id ? setExpandedId(null) : fetchGrantDetails(grant.id);
                        }}
                        className="text-[#E04E39] hover:text-[#E86B5A] transition-colors"
                      >
                        {expandedId === grant.id ? <CaretUp size={20} /> : <CaretDown size={20} />}
                      </button>
                    </td>
                  </tr>
                  {expandedId === grant.id && selectedGrant && (
                    <tr className="bg-muted/30">
                      <td colSpan="8" className="px-6 py-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                          <div>
                            <h4 className="text-base font-bold mb-4 uppercase tracking-wider text-muted-foreground">Grant Details</h4>
                            <div className="grid grid-cols-2 gap-4">
                              <div className="p-3 bg-background border border-border rounded">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Budget</p>
                                <p className="text-sm font-bold">${selectedGrant.budget.toLocaleString()}</p>
                              </div>
                              <div className="p-3 bg-background border border-border rounded">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Actual Spend</p>
                                <p className="text-sm font-bold text-[#E04E39]">${selectedGrant.spent.toLocaleString()}</p>
                              </div>
                              <div className="p-3 bg-background border border-border rounded">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Remaining</p>
                                <p className="text-sm font-bold">${(selectedGrant.budget - selectedGrant.spent).toLocaleString()}</p>
                              </div>
                              <div className="p-3 bg-background border border-border rounded">
                                <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-1">Start Date</p>
                                <p className="text-sm font-bold">{new Date(selectedGrant.start_date).toLocaleDateString()}</p>
                              </div>
                            </div>
                          </div>

                          <div>
                            <h4 className="text-base font-bold mb-4 uppercase tracking-wider text-muted-foreground">Compliance & Documents</h4>
                            <div className="space-y-4">
                              <div>
                                <p className="text-xs text-muted-foreground font-medium mb-2">Required Documents</p>
                                <div className="flex flex-wrap gap-2">
                                  {selectedGrant.documents.map((doc, idx) => (
                                    <span key={idx} className="text-xs px-2 py-1 bg-muted rounded border border-border">{doc}</span>
                                  ))}
                                </div>
                              </div>
                              
                              {selectedGrant.alerts.length > 0 && (
                                <div>
                                  <p className="text-xs text-muted-foreground font-medium mb-2">System Alerts</p>
                                  <div className="space-y-2">
                                    {selectedGrant.alerts.map((alert, idx) => (
                                      <div key={idx} className="flex items-start gap-2 p-3 bg-[#f59e0b]/10 border border-[#f59e0b]/30 rounded">
                                        <Warning size={16} className="text-[#f59e0b] mt-0.5 shrink-0" />
                                        <p className="text-xs font-medium">{alert}</p>
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </div>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showScanModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
          <div className="bg-card border border-border rounded-md w-full max-w-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
            <div className="p-6 border-b border-border flex justify-between items-center bg-muted/50">
              <div className="flex items-center gap-3">
                <h2 className="text-lg sm:text-xl font-bold">
                  {scanning ? 'AI Analyzing...' : 'Import Grant'}
                </h2>
                {scanning && <Sparkle size={24} weight="fill" className="text-[#E04E39] animate-spin" />}
              </div>
              <button onClick={() => setShowScanModal(false)} className="text-muted-foreground hover:text-foreground">
                <X size={24} />
              </button>
            </div>

            <div className="overflow-y-auto flex-1 p-4 sm:p-6">
              {scanning ? (
                <div className="py-12 flex flex-col items-center justify-center space-y-4">
                  <div className="w-full max-w-xs h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full bg-[#E04E39] animate-[shimmer_2s_infinite_linear] w-1/2" style={{ background: 'linear-gradient(90deg, transparent, #E04E39, transparent)' }}></div>
                  </div>
                  <p className="text-muted-foreground animate-pulse text-center text-sm">VANCO AI is extracting terms, dates, and amounts...</p>
                </div>
              ) : (
                <form onSubmit={handleSaveScannedGrant} className="space-y-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="sm:col-span-2">
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Grant Name</label>
                      <input
                        type="text"
                        required
                        value={scannedGrant.name}
                        onChange={(e) => setScannedGrant({...scannedGrant, name: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Funder</label>
                      <input
                        type="text"
                        required
                        value={scannedGrant.funder}
                        onChange={(e) => setScannedGrant({...scannedGrant, funder: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Total Amount ($)</label>
                      <input
                        type="number"
                        required
                        value={scannedGrant.amount}
                        onChange={(e) => setScannedGrant({...scannedGrant, amount: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Start Date</label>
                      <input
                        type="date"
                        required
                        value={scannedGrant.start_date}
                        onChange={(e) => setScannedGrant({...scannedGrant, start_date: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">End Date</label>
                      <input
                        type="date"
                        required
                        value={scannedGrant.end_date}
                        onChange={(e) => setScannedGrant({...scannedGrant, end_date: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Reporting Deadline</label>
                      <input
                        type="date"
                        required
                        value={scannedGrant.reporting_deadline}
                        onChange={(e) => setScannedGrant({...scannedGrant, reporting_deadline: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-[#f59e0b]/50 rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-muted-foreground uppercase mb-1">Sector</label>
                      <select
                        required
                        value={scannedGrant.sector}
                        onChange={(e) => setScannedGrant({...scannedGrant, sector: e.target.value})}
                        className="w-full px-4 py-2 bg-background border border-border rounded text-foreground focus:border-[#E04E39] outline-none text-sm"
                      >
                        <option value="">Select Sector</option>
                        {["Education", "Healthcare", "Environment", "Human Rights", "LGBTQ+ Rights", "Women Empowerment", "Youth Development"].map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  
                  <div className="pt-4 flex flex-col sm:flex-row gap-3 border-t border-border">
                    <button
                      type="button"
                      onClick={() => setShowScanModal(false)}
                      className="order-2 sm:order-1 flex-1 px-4 py-3 bg-transparent border border-border rounded hover:bg-muted font-medium transition-all text-sm"
                    >
                      Discard
                    </button>
                    <button
                      type="submit"
                      className="order-1 sm:order-2 flex-1 px-4 py-3 bg-[#E04E39] hover:bg-[#E86B5A] text-white rounded font-bold shadow-lg flex items-center justify-center gap-2 transition-all text-sm"
                    >
                      <Check size={20} weight="bold" />
                      APPROVE & TRACK
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrantTracker;
